import React, { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Pencil, Trash2, FileStack } from "lucide-react";
import { fmtLocalDT as fmtDT } from "@/lib/clinical";

const summarise = (values = {}) =>
  Object.entries(values).filter(([k, v]) => k !== "report" && v !== "" && v != null).map(([k, v]) => `${k}: ${v}`).join(" · ");

export function RowActions({ row, onEdit, onDelete }) {
  return (
    <div className="flex items-center gap-1 justify-end">
      <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => onEdit(row)} title="Edit" data-testid={`inv-edit-${row.id}`}><Pencil size={13} /></Button>
      <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={() => onDelete(row)} title="Delete" data-testid={`inv-delete-${row.id}`}><Trash2 size={13} /></Button>
    </div>
  );
}

export default function InvestigationReports({ rows, canManage, onEdit, onDelete, renderFiles }) {
  const [cat, setCat] = useState("All");
  const counts = useMemo(() => rows.reduce((m, r) => ({ ...m, [r.category]: (m[r.category] || 0) + 1 }), {}), [rows]);
  const shown = cat === "All" ? rows : rows.filter((r) => r.category === cat);

  return (
    <div className="card-clinical p-4" data-testid="inv-all-reports">
      <div className="flex items-center gap-2 mb-3">
        <FileStack size={15} className="text-primary" />
        <div className="text-sm font-medium">All investigation reports ({rows.length})</div>
      </div>
      <div className="flex flex-wrap gap-2 mb-3" data-testid="inv-all-filter">
        {["All", ...Object.keys(counts)].map((c) => (
          <button key={c} type="button" onClick={() => setCat(c)} data-testid={`inv-all-filter-${c}`}
            className={`px-3 py-1 rounded-full text-xs border transition-colors ${cat === c ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:bg-muted"}`}>
            {c} <span className="opacity-70">{c === "All" ? rows.length : counts[c]}</span>
          </button>
        ))}
      </div>
      <div className="overflow-auto">
        <table className="vitals-grid w-full tabular">
          <thead><tr><th>Date & time</th><th>Investigation</th><th>Result / report</th><th>By</th>{canManage && <th />}</tr></thead>
          <tbody>
            {shown.length === 0 && <tr><td colSpan={5} className="text-center text-muted-foreground py-6" data-testid="inv-all-empty">No investigation reports recorded yet</td></tr>}
            {shown.map((r) => (
              <tr key={r.id} data-testid={`inv-all-row-${r.id}`}>
                <td className="whitespace-nowrap">{fmtDT(r.at)}</td>
                <td><Badge variant="secondary" className="whitespace-nowrap">{r.category}</Badge></td>
                <td className="whitespace-pre-wrap text-left">
                  {r.values?.cancelled ? <span className="text-muted-foreground italic">Cancelled</span> : (
                    <>
                      {summarise(r.values) && <div>{summarise(r.values)}</div>}
                      {r.values?.report && <div className="text-sm">{r.values.report}</div>}
                    </>
                  )}
                  {(r.file_ids || []).length > 0 && renderFiles(r.file_ids)}
                </td>
                <td className="text-muted-foreground text-xs">{r.edited_by ? `${r.by} · edited by ${r.edited_by}` : r.by}</td>
                {canManage && <td><RowActions row={r} onEdit={onEdit} onDelete={onDelete} /></td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
